import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Search, Star, Users, Eye, Edit3, X,
  LayoutTemplate, Crown, CheckCircle
} from 'lucide-react';
import { mockTemplates } from '../services/mockData';
import { useTranslation } from '../i18n/useTranslation';
import { useAppStore } from '../stores/appStore';
import type { Template, TemplateCategory } from '../types';
import { clsx } from 'clsx';
import { firestoreService, isFirebaseConfigured } from '../services/firebase';

const categoryEmojis: Array<{ id: TemplateCategory | 'all'; emoji: string }> = [
  { id: 'all', emoji: '✨' },
  { id: 'portfolio', emoji: '🎨' },
  { id: 'business', emoji: '🏢' },
  { id: 'blog', emoji: '📝' },
  { id: 'landing', emoji: '🚀' },
  { id: 'store', emoji: '🛍️' },
  { id: 'personal', emoji: '👤' },
];

const categoryColors: Record<TemplateCategory, string> = {
  portfolio: 'bg-purple-100 text-purple-700',
  business: 'bg-blue-100 text-blue-700',
  blog: 'bg-yellow-100 text-yellow-700',
  landing: 'bg-red-100 text-red-700',
  store: 'bg-green-100 text-green-700',
  personal: 'bg-pink-100 text-pink-700',
};

const templateThumbs: Record<string, { bg: string; preview: React.ReactNode }> = {
  'portfolio-minimal': {
    bg: 'from-slate-900 to-slate-800',
    preview: (
      <div className="w-full h-full flex flex-col items-center justify-center p-3 text-center">
        <div className="w-8 h-8 rounded-full bg-indigo-500 mb-2" />
        <div className="text-white text-xs font-bold mb-1">Ahmad Design</div>
        <div className="text-slate-400 text-xs mb-2">Designer & Developer</div>
        <div className="w-16 h-5 bg-indigo-500 rounded text-white text-xs flex items-center justify-center">Hubungi</div>
      </div>
    ),
  },
  'business-pro': {
    bg: 'from-blue-900 to-blue-800',
    preview: (
      <div className="w-full h-full flex flex-col">
        <div className="bg-white px-2 py-1 flex justify-between items-center">
          <div className="text-blue-700 text-xs font-bold">SyarikatMaju</div>
          <div className="flex gap-1">
            {[1,2,3].map(i => <div key={i} className="w-4 h-1.5 bg-slate-200 rounded" />)}
          </div>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center p-2 text-center">
          <div className="text-white text-xs font-bold mb-1">Penyelesaian Perniagaan</div>
          <div className="w-14 h-4 bg-white rounded text-blue-700 text-xs flex items-center justify-center">Hubungi</div>
        </div>
      </div>
    ),
  },
  'blog-clean': {
    bg: 'from-amber-50 to-white',
    preview: (
      <div className="w-full h-full p-2 flex flex-col">
        <div className="border-b border-amber-200 pb-1 mb-2">
          <div className="text-red-600 text-xs font-bold">Blog Saya</div>
        </div>
        <div className="bg-white rounded p-1.5 mb-1.5 shadow-sm">
          <div className="w-full h-1.5 bg-slate-300 rounded mb-1" />
          <div className="w-3/4 h-1.5 bg-slate-200 rounded" />
        </div>
        {[1,2,3].map(i => (
          <div key={i} className="flex items-center gap-1 mb-1">
            <div className="w-2/3 h-1.5 bg-slate-200 rounded" />
          </div>
        ))}
      </div>
    ),
  },
  'landing-bold': {
    bg: 'from-violet-900 to-violet-800',
    preview: (
      <div className="w-full h-full flex flex-col items-center justify-center p-3 text-center">
        <div className="w-12 h-3 bg-white/20 rounded-full mb-2" />
        <div className="text-white text-xs font-bold mb-2">Ubah Cara Anda Bekerja</div>
        <div className="flex gap-1">
          <div className="w-12 h-4 bg-white rounded text-violet-700 text-xs flex items-center justify-center font-bold">Cuba</div>
          <div className="w-10 h-4 border border-white/40 rounded text-white text-xs flex items-center justify-center">Info</div>
        </div>
      </div>
    ),
  },
  'store-grid': {
    bg: 'from-emerald-50 to-white',
    preview: (
      <div className="w-full h-full flex flex-col">
        <div className="bg-white px-2 py-1 border-b border-emerald-100 flex justify-between">
          <div className="text-emerald-600 text-xs font-bold">🛍️ Kedai Saya</div>
          <div className="w-8 h-3 bg-emerald-500 rounded text-white text-xs flex items-center justify-center">Order</div>
        </div>
        <div className="grid grid-cols-2 gap-1 p-1.5 flex-1">
          {['👕','👖','👟','🧢'].map((e,i) => (
            <div key={i} className="bg-white rounded shadow-sm flex flex-col items-center justify-center p-1">
              <div className="text-base">{e}</div>
              <div className="w-8 h-1 bg-slate-200 rounded mt-0.5" />
            </div>
          ))}
        </div>
      </div>
    ),
  },
  'personal-linktree': {
    bg: 'from-violet-900 to-purple-800',
    preview: (
      <div className="w-full h-full flex flex-col items-center justify-center p-2">
        <div className="w-8 h-8 rounded-full bg-white/20 border-2 border-white/40 flex items-center justify-center text-sm mb-1.5">🌟</div>
        <div className="text-white text-xs font-bold mb-1">Nama Saya</div>
        <div className="w-full space-y-1">
          {[1,2,3].map(i => (
            <div key={i} className="w-full h-4 bg-white/15 rounded-lg border border-white/20" />
          ))}
        </div>
      </div>
    ),
  },
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1,2,3,4,5].map(i => (
        <Star
          key={i}
          size={12}
          className={i <= Math.floor(rating) ? 'text-yellow-400 fill-yellow-400' : 'text-surface-300'}
        />
      ))}
      <span className="text-xs text-surface-500 ml-1">{rating.toFixed(1)}</span>
    </div>
  );
}

export default function GalleryPage() {
  const navigate = useNavigate();
  const { setSelectedTemplate, setAllTemplateVariables, setCurrentHtml, showToast } = useAppStore();
  const { t } = useTranslation();

  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<TemplateCategory | 'all'>('all');
  const [previewTemplate, setPreviewTemplate] = useState<Template | null>(null);
  const [remoteTemplates, setRemoteTemplates] = useState<Template[]>([]);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(isFirebaseConfigured);
  useEffect(() => { if (!isFirebaseConfigured) return; let active = true; firestoreService.getTemplates().then((items) => { if (active) setRemoteTemplates(items); }).catch((e) => { if (active) setLoadError(e.message); }).finally(() => { if (active) setLoading(false); }); return () => { active = false; }; }, []);

  const templates = isFirebaseConfigured ? remoteTemplates : mockTemplates;
  const filtered = templates.filter((t) => {
    const matchCat = activeCategory === 'all' || t.category === activeCategory;
    const matchSearch = t.name.toLowerCase().includes(search.toLowerCase()) ||
      t.description.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const handleUseTemplate = (template: Template) => {
    setSelectedTemplate(template);
    const defaults: Record<string, string> = {};
    template.variables.forEach((v) => { defaults[v.key] = v.defaultValue; });
    setAllTemplateVariables(defaults);
    setCurrentHtml(template.htmlContent);
    showToast(`"${template.name}" ${t('gallery.selected')}`, 'success');
    navigate('/editor');
  };

  const catLabel = (id: string) => {
    const map: Record<string, string> = {
      all: t('gallery.all'), portfolio: t('gallery.portfolio'), business: t('gallery.business'),
      blog: t('gallery.blog'), landing: t('gallery.landing'), store: t('gallery.store'), personal: t('gallery.personal'),
    };
    return map[id] || id;
  };

  return (
    <div className="min-h-screen bg-surface-50">
      {/* Header */}
      <div className="bg-white border-b border-surface-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-3 mb-2">
            <LayoutTemplate size={28} className="text-telegram" />
            <h1 className="text-3xl font-extrabold text-surface-900">{t('gallery.title')}</h1>
          </div>
          <p className="text-surface-500">{t('gallery.sub')}</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Search & Filter */}
        {loading && <p role="status">Memuatkan reka bentuk...</p>}
        {loadError && <p role="alert" className="text-red-600">{loadError}</p>}
        <div className="flex flex-col sm:flex-row gap-4 mb-8">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-400" />
            <input
              type="text"
              placeholder={t('gallery.search')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-8">
          {categoryEmojis.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={clsx(
                'flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all',
                activeCategory === cat.id
                  ? 'bg-telegram text-white shadow-glow'
                  : 'bg-white text-surface-600 hover:bg-surface-100 border border-surface-200'
              )}
            >
              <span>{cat.emoji}</span>
              {catLabel(cat.id)}
            </button>
          ))}
        </div>

        {/* Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeCategory + search}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {filtered.map((template, i) => (
              <motion.div
                key={template.templateId}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.07 }}
                className="card card-hover group"
              >
                {/* Preview Thumbnail */}
                <div className={`template-card-preview bg-gradient-to-br ${templateThumbs[template.templateId]?.bg || 'from-slate-100 to-slate-200'}`}>
                  {templateThumbs[template.templateId]?.preview || <iframe title={template.name} srcDoc={template.htmlContent} sandbox="" className="w-full h-full pointer-events-none" />}
                  {template.isPremium && (
                    <div className="absolute top-3 right-3 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full flex items-center gap-1">
                      <Crown size={10} />
                      PRO
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center opacity-0 group-hover:opacity-100">
                    <button
                      onClick={() => setPreviewTemplate(template)}
                      className="btn bg-white text-surface-900 btn-sm font-semibold shadow-lg"
                    >
                      <Eye size={14} />
                      {t('gallery.preview_full')}
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div>
                      <h3 className="font-bold text-surface-900">{template.name}</h3>
                      <span className={clsx('badge text-xs mt-1', categoryColors[template.category])}>
                        {template.category}
                      </span>
                    </div>
                    {!template.isPremium && (
                      <span className="badge-green text-xs">{t('gallery.free')}</span>
                    )}
                  </div>

                  <p className="text-surface-500 text-sm mb-3 line-clamp-2">{template.description}</p>

                  <div className="flex items-center justify-between mb-4">
                    <StarRating rating={template.rating} />
                    <div className="flex items-center gap-1 text-xs text-surface-400">
                      <Users size={12} />
                      {template.usageCount}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-1 mb-4">
                    {template.tags.map((tag) => (
                      <span key={tag} className="badge-gray text-xs">{tag}</span>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => setPreviewTemplate(template)}
                      className="btn-outline btn-sm flex-1"
                    >
                      <Eye size={14} />
                      {t('gallery.preview')}
                    </button>
                    <button
                      onClick={() => handleUseTemplate(template)}
                      className="btn-primary btn-sm flex-1"
                    >
                      <Edit3 size={14} />
                      {t('gallery.use')}
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatePresence>

        {filtered.length === 0 && (
          <div className="text-center py-20">
            <div className="text-5xl mb-4">🔍</div>
            <h3 className="text-xl font-bold text-surface-700 mb-2">{t('gallery.empty_title')}</h3>
            <p className="text-surface-500">{t('gallery.empty_sub')}</p>
          </div>
        )}

        {/* Import HTML CTA */}
        <div className="mt-12 card p-6 bg-gradient-to-r from-telegram/5 to-blue-50 border-telegram/20">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 telegram-gradient rounded-xl flex items-center justify-center">
                <span className="text-white text-xl">📥</span>
              </div>
              <div>
                <h3 className="font-bold text-surface-900">{t('gallery.import_title')}</h3>
                <p className="text-surface-500 text-sm">{t('gallery.import_sub')}</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/import')}
              className="btn-primary btn-md flex-shrink-0"
            >
              {t('gallery.import_cta')}
            </button>
          </div>
        </div>
      </div>

      {/* ─── Preview Modal ─────────────────────────────────────────── */}
      <AnimatePresence>
        {previewTemplate && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setPreviewTemplate(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-surface-200">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 bg-telegram/10 rounded-lg flex items-center justify-center">
                    <Eye size={16} className="text-telegram" />
                  </div>
                  <div>
                    <h3 className="font-bold text-surface-900">{previewTemplate.name}</h3>
                    <div className="flex items-center gap-2">
                      <StarRating rating={previewTemplate.rating} />
                      <span className={clsx('badge text-xs', categoryColors[previewTemplate.category])}>
                        {previewTemplate.category}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      handleUseTemplate(previewTemplate);
                      setPreviewTemplate(null);
                    }}
                    className="btn-primary btn-sm"
                  >
                    <Edit3 size={14} />
                    {t('gallery.use_this')}
                  </button>
                  <button
                    onClick={() => setPreviewTemplate(null)}
                    className="p-2 rounded-lg hover:bg-surface-100 text-surface-500"
                  >
                    <X size={18} />
                  </button>
                </div>
              </div>

              {/* iframe Preview */}
              <div className="flex-1 overflow-hidden rounded-b-2xl bg-surface-100">
                <iframe
                  srcDoc={previewTemplate.htmlContent.replace(/\{\{(\w+)\}\}/g, (_, key) => {
                    const variable = previewTemplate.variables.find(v => v.key === key);
                    return variable?.defaultValue || `{{${key}}}`;
                  })}
                  sandbox="allow-same-origin"
                  className="w-full h-full border-0"
                  style={{ minHeight: '500px' }}
                  title={`Preview ${previewTemplate.name}`}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
